import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Row, View } from "@amzn/stencil-react-components/layout";
import {
  MessageBanner,
  MessageBannerType,
} from "@amzn/stencil-react-components/message-banner";
import { Text, TextVariant } from "@amzn/stencil-react-components/text";
import { FormattedMessage } from "react-intl";

import {
  publishSecurityKeyIssueReportedMetric,
  publishSecurityKeyStepSkippedMetric,
} from "@/apis/Metrics";

import dog from "../../assets/images/dog.svg";

/**
 * Each type which corresponds to a different failure scenario with specific user guidance.
 *
 * - `usb`: USB device access failures (permissions, browser support)
 * - `non_functional_security_key`: Security key hardware or usage issues
 * - `usb_detection_timeout`: Security key not detected within expected timeframe
 */
export type SecurityKeyErrorType =
  | "usb"
  | "non_functional_security_key"
  | "usb_detection_timeout";

/**
 * Props for the SecurityKeyUsbErrorScreen component.
 */
interface SecurityKeyUsbErrorProps {
  /** Callback function to open the issue reporting modal */
  onReportIssue: () => void;
  /** Callback function to skip security key testing and continue onboarding */
  onContinueWithoutTesting: () => void;
  /** Type of error to determine appropriate messaging and troubleshooting steps */
  errorType?: SecurityKeyErrorType;
}

/**
 * Configuration for error messages and troubleshooting steps
 */
const ERROR_CONFIG = {
  usb: {
    messageKey: "insertAndTestSecurityKey-error-usbErrorMessage",
    troubleshootingSteps: [
      "insertAndTestSecurityKey-error-troubleshootingStep-usb1",
      "insertAndTestSecurityKey-error-troubleshootingStep-usb2",
      "insertAndTestSecurityKey-error-troubleshootingStep-usb3",
    ],
    recommendationKey: "insertAndTestSecurityKey-error-recommendation-usb",
  },
  non_functional_security_key: {
    messageKey: "insertAndTestSecurityKey-error-nonFunctionalMessage",
    troubleshootingSteps: [
      "insertAndTestSecurityKey-error-troubleshootingStep-nonFunctional1",
      "insertAndTestSecurityKey-error-troubleshootingStep-nonFunctional2",
    ],
    recommendationKey:
      "insertAndTestSecurityKey-error-recommendation-nonFunctional",
  },
  usb_detection_timeout: {
    messageKey: "insertAndTestSecurityKey-error-detectionTimeoutMessage",
    troubleshootingSteps: [
      "insertAndTestSecurityKey-error-troubleshootingStep-timeout1",
    ],
    recommendationKey: "insertAndTestSecurityKey-error-recommendation-timeout",
  },
};

/**
 * Custom hook to get error configuration based on error type.
 *
 * @param errorType - The type of error that occurred
 * @returns Configuration object with message, troubleshooting steps, and recommendations
 */
const useErrorConfig = (errorType: SecurityKeyErrorType) => {
  const config = ERROR_CONFIG[errorType];

  return {
    messageKey: config.messageKey,
    troubleshootingSteps: [...config.troubleshootingSteps],
    recommendationKey: config.recommendationKey,
  };
};

/**
 * Reusable troubleshooting section component that displays helpful guidance.
 *
 * @param props - Component props
 * @param props.steps - Array of i18n keys for troubleshooting steps to display as a bulleted list
 * @returns JSX element containing the troubleshooting guidance section
 */
const TroubleshootingSection = ({ steps }: { steps: string[] }) => (
  <View
    backgroundColor="color.surface.bg-default"
    padding="dimensions.spacing.400"
    borderRadius="dimensions.border.radius.200"
  >
    <Row columnGap="dimensions.spacing.400" alignItems="flex-start">
      <Col flex={1} gridGap="dimensions.spacing.200">
        <Text variant={TextVariant.HeadingSM}>
          <FormattedMessage id="insertAndTestSecurityKey-error-troubleshootingTitle" />
        </Text>
        <ul style={{ margin: 0, paddingLeft: "1.5rem" }}>
          {steps.map((stepKey, index) => (
            <li key={index} style={{ marginBottom: "0.75rem" }}>
              <Text>
                <FormattedMessage id={stepKey} />
              </Text>
            </li>
          ))}
        </ul>
      </Col>
      <Col>
        <img
          src={dog}
          alt="Troubleshooting illustration"
          style={{ flexShrink: 0, maxWidth: "120px", height: "auto" }}
        />
      </Col>
    </Row>
  </View>
);

/**
 * Error state component for security key related errors and troubleshooting guidance.
 *
 * This component handles various security key failure scenarios and provides users
 * with appropriate error messages, troubleshooting steps, and recovery options.
 * It serves as a comprehensive error handling interface that helps users resolve
 * issues or find alternative paths to continue their onboarding.
 *
 * Supported Error Types:
 * - **USB Access Errors**: Browser permissions, system restrictions, service unavailability
 * - **Non-Functional Security Key**: Hardware issues, incorrect usage, OTP generation failures
 * - **Detection Timeout**: Security key not detected within expected timeframe
 *
 * User Actions:
 * - **Report Issue**: Opens issue reporting modal for assistance
 * - **Continue Without Testing**: Skips security key validation step
 *
 * The component automatically publishes appropriate metrics when users interact
 * with the error recovery options, helping track common issues and user behavior.
 *
 * @param props - The component props
 * @param props.onReportIssue - Function to open the issue reporting workflow
 * @param props.onContinueWithoutTesting - Function to skip security key testing
 * @param props.errorMessage - Optional custom error message to display
 * @param props.errorType - Type of error to determine messaging and troubleshooting
 * @returns JSX element containing the error screen with troubleshooting guidance
 */
export function SecurityKeyUsbErrorScreen({
  onReportIssue,
  onContinueWithoutTesting,
  errorType = "non_functional_security_key",
}: SecurityKeyUsbErrorProps): JSX.Element {
  const { messageKey, troubleshootingSteps, recommendationKey } =
    useErrorConfig(errorType);

  /**
   * Handles the "Report Issue" button click with metrics collection.
   */
  const handleReportIssue = () => {
    publishSecurityKeyIssueReportedMetric();
    onReportIssue();
  };

  /**
   * Handles the "Continue Without Testing" button click with metrics collection.
   */
  const handleContinueWithoutTesting = () => {
    publishSecurityKeyStepSkippedMetric(errorType === "usb_detection_timeout");
    onContinueWithoutTesting();
  };

  return (
    <Col gridGap="dimensions.spacing.400">
      <MessageBanner
        type={MessageBannerType.Error}
        dataTestId="securityKeyErrorBanner"
      >
        <Text>{<FormattedMessage id={messageKey} />}</Text>
      </MessageBanner>
      <TroubleshootingSection steps={troubleshootingSteps} />
      <Text>
        <FormattedMessage id={recommendationKey} />
      </Text>
      <Row columnGap="dimensions.spacing.300" rowGap="dimensions.spacing.300">
        <Button onClick={handleReportIssue} variant={ButtonVariant.Secondary}>
          <FormattedMessage id="insertAndTestSecurityKey-error-reportIssueButton" />
        </Button>
        <Button
          onClick={handleContinueWithoutTesting}
          variant={ButtonVariant.Tertiary}
        >
          <FormattedMessage id="insertAndTestSecurityKey-error-continueWithoutTestingButton" />
        </Button>
      </Row>
    </Col>
  );
}
